import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn, Sheet, SheetContent, SheetHeader, SheetTitle } from '@proerp/ui';
import { useNavGroupOrder, useNavItems } from './useNavItems';

/** Aktiv, wenn der Pfad exakt passt oder darunter liegt (z. B. /verkauf/123). */
function matches(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Mobile Hauptnavigation als Bottom-Tab-Bar; nur unterhalb des `tablet`-Breakpoints (744px) sichtbar. */
export function BottomNav() {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const navItems = useNavItems();
  const groupOrder = useNavGroupOrder();
  const { pathname } = useLocation();
  // Max. 5 Slots in der Bottom-Bar (erp-design-guideline.md §5.2): 4 primäre + „Mehr".
  const primary = navItems.slice(0, 4);
  const primaryPaths = new Set(primary.map((item) => item.to));
  // „Mehr" hervorheben, wenn die aktive Seite nicht zu den 4 primären gehört.
  const moreActive = navItems.some((item) => !primaryPaths.has(item.to) && matches(pathname, item.to));

  // Vollständiges Menü im Sheet — nach Abschnitten gruppiert wie die Desktop-Sidebar.
  const grouped = groupOrder
    .map((group) => ({
      group,
      items: navItems.filter((item) => item.group === group),
    }))
    .filter((entry) => entry.items.length > 0);

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 shadow-[0_-1px_4px_rgba(0,0,0,0.06)] backdrop-blur-md pb-[env(safe-area-inset-bottom)] tablet:hidden"
        aria-label="Hauptnavigation"
      >
        <div className="flex h-16">
          {primary.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'relative flex flex-1 select-none touch-manipulation flex-col items-center justify-center gap-1 text-[0.6875rem] font-medium transition-colors active:bg-muted/70',
                  isActive ? 'text-accent-600' : 'text-muted-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute inset-x-[28%] top-0 h-[3px] rounded-full bg-accent-600" aria-hidden />
                  )}
                  <item.icon className="size-6" strokeWidth={isActive ? 2 : 1.5} aria-hidden />
                  <span className="max-w-full truncate px-1">{item.short}</span>
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setIsMoreOpen(true)}
            aria-label="Weitere Module"
            aria-expanded={isMoreOpen}
            className={cn(
              'relative flex flex-1 select-none touch-manipulation flex-col items-center justify-center gap-1 text-[0.6875rem] font-medium transition-colors active:bg-muted/70',
              moreActive ? 'text-accent-600' : 'text-muted-foreground',
            )}
          >
            {moreActive && (
              <span className="absolute inset-x-[28%] top-0 h-[3px] rounded-full bg-accent-600" aria-hidden />
            )}
            <MoreHorizontal className="size-6" strokeWidth={moreActive ? 2 : 1.5} aria-hidden />
            <span>Mehr</span>
          </button>
        </div>
      </nav>

      <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
        <SheetContent side="bottom" className="max-h-[82vh] overflow-y-auto rounded-t-xl p-2 pb-[max(1.5rem,env(safe-area-inset-bottom))] tablet:hidden">
          <SheetHeader className="px-3 py-2 text-left">
            <SheetTitle className="text-md font-medium text-foreground">Navigation</SheetTitle>
          </SheetHeader>
          <nav aria-label="Alle Module" className="flex flex-col gap-4 pb-2">
            {grouped.map(({ group, items }) => (
              <div key={group}>
                <p className="px-3 pb-1 text-[0.6875rem] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  {group}
                </p>
                <div className="flex flex-col">
                  {items.map((item) => (
                    <div key={item.to}>
                      <NavLink
                        to={item.to}
                        end={Boolean(item.children?.length) || item.to === '/'}
                        onClick={() => setIsMoreOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            'flex h-12 select-none touch-manipulation items-center gap-3 rounded-lg px-3 text-[15px] transition-colors active:bg-muted',
                            isActive
                              ? 'bg-accent font-medium text-accent-foreground'
                              : 'text-foreground hover:bg-muted/60',
                          )
                        }
                      >
                        <item.icon className="size-5 shrink-0" strokeWidth={1.5} aria-hidden />
                        <span className="flex-1">{item.label}</span>
                        {!item.ready && <span className="text-xs text-muted-foreground">bald</span>}
                      </NavLink>
                      {item.children
                        ?.filter((child) => child.to !== item.to)
                        .map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            end
                            onClick={() => setIsMoreOpen(false)}
                            className={({ isActive }) =>
                              cn(
                                'flex h-11 select-none touch-manipulation items-center rounded-lg pl-11 pr-3 text-sm transition-colors active:bg-muted',
                                isActive ? 'font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60',
                              )
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
}
